import { For, Show, type Component } from 'solid-js';
import type { OddsTier, Range, SpreadAnalysis } from '~/domain/types';
import { BET_TYPE_LABELS } from '~/domain/bets';
import { formatCents } from '~/domain/profiles';

/**
 * The §2.5 odds breakdown: stake grouped by payout tier at the active table.
 *
 * Grouped by payout rather than bet type because several bet types share a
 * payout, and what the user is reading is the shape of the stake distribution.
 * The total row carries net-if-one-misses — the figure that is easy to lose
 * sight of while reading a column of winning odds.
 */

export interface OddsBreakdownProps {
  analysis: SpreadAnalysis;
  currency: string;
}

const formatRange = (range: Range, currency: string): string => {
  const low = formatCents(range.min, currency);
  return range.min === range.max ? low : `${low} to ${formatCents(range.max, currency)}`;
};

const signed = (cents: number, currency: string) =>
  `${cents > 0 ? '+' : ''}${formatCents(cents, currency)}`;

const signedRange = (range: Range, currency: string): string =>
  range.min === range.max
    ? signed(range.min, currency)
    : `${signed(range.min, currency)} to ${signed(range.max, currency)}`;

const OddsBreakdown: Component<OddsBreakdownProps> = (props) => {
  const breakdown = () => props.analysis.oddsBreakdown;

  return (
    <section class="rounded-lg border border-line bg-surface-1 p-3">
      <h2 class="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
        Odds breakdown
      </h2>

      <Show
        when={breakdown().tiers.length > 0}
        fallback={<p class="text-[11px] text-ink-faint">No chips placed yet.</p>}
      >
        <div class="overflow-x-auto">
          <table class="w-full min-w-[420px] border-collapse text-[11px]">
            <thead>
              <tr class="border-b border-line text-left text-[10px] uppercase tracking-wide text-ink-faint">
                <th class="pb-1 pr-2 font-medium">Odds</th>
                <th class="pb-1 pr-2 font-medium">Bets</th>
                <th class="pb-1 pr-2 text-right font-medium">Chips</th>
                <th class="pb-1 pr-2 text-right font-medium">Stake</th>
                <th class="pb-1 pr-2 text-right font-medium">Pockets</th>
                <th class="pb-1 text-right font-medium">Net if one hits</th>
              </tr>
            </thead>
            <tbody>
              <For each={breakdown().tiers}>{(tier) => <TierRow tier={tier} currency={props.currency} />}</For>
            </tbody>
            <tfoot>
              <tr class="border-t border-line-strong font-medium">
                <td class="pt-1.5 pr-2 text-ink">Total</td>
                <td class="tnum pt-1.5 pr-2 text-ink">{breakdown().totalBets}</td>
                <td class="tnum pt-1.5 pr-2 text-right text-ink">{breakdown().totalChips}</td>
                <td class="tnum pt-1.5 pr-2 text-right text-ink">
                  {formatCents(breakdown().totalStakeCents, props.currency)}
                </td>
                <td class="tnum pt-1.5 pr-2 text-right text-ink">
                  {breakdown().pocketsCovered}/{breakdown().pocketCount}
                </td>
                <td class="pt-1.5 text-right">
                  <Show
                    when={breakdown().netIfNoneHitCents}
                    fallback={<span class="text-[10px] text-ink-faint">no pocket misses</span>}
                  >
                    {(miss) => (
                      <span class="tnum text-loss">
                        {signedRange(miss(), props.currency)}
                      </span>
                    )}
                  </Show>
                </td>
              </tr>
              <tr>
                <td colspan="5" />
                <td class="pt-0.5 text-right text-[10px] text-ink-faint">net if one misses</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <Show when={(breakdown().netIfNoneHitCents?.min ?? 0) !== (breakdown().netIfNoneHitCents?.max ?? 0)}>
          <p class="mt-1.5 text-[10px] leading-relaxed text-ink-faint">
            A miss costs a range rather than one figure because this table's zero rule
            refunds half a losing even-money stake — so a missed zero costs less than a
            missed number.
          </p>
        </Show>
      </Show>
    </section>
  );
};

const TierRow: Component<{ tier: OddsTier; currency: string }> = (props) => (
  <tr class="border-b border-line/60 last:border-0">
    <td class="tnum py-1 pr-2 font-medium text-ink">{props.tier.payout}:1</td>
    <td class="py-1 pr-2 text-ink-dim">
      {props.tier.betTypes.map((betType) => BET_TYPE_LABELS[betType]).join(', ')}
      <span class="tnum ml-1 text-ink-faint">×{props.tier.bets}</span>
    </td>
    <td class="tnum py-1 pr-2 text-right text-ink">{props.tier.chips}</td>
    <td class="py-1 pr-2 text-right">
      <span class="tnum text-ink">{formatCents(props.tier.stakeCents, props.currency)}</span>
      <span class="tnum ml-1 text-[10px] text-ink-faint">
        {(props.tier.stakeShare * 100).toFixed(0)}%
      </span>
    </td>
    <td class="tnum py-1 pr-2 text-right text-ink-dim">{props.tier.pockets}</td>
    <td
      class="tnum py-1 text-right"
      classList={{
        'text-gain': props.tier.netIfHitCents.min > 0,
        'text-loss': props.tier.netIfHitCents.max < 0,
        'text-ink-dim':
          props.tier.netIfHitCents.min <= 0 && props.tier.netIfHitCents.max >= 0,
      }}
      title={formatRange(props.tier.netIfHitCents, props.currency)}
    >
      {signedRange(props.tier.netIfHitCents, props.currency)}
    </td>
  </tr>
);

export default OddsBreakdown;
